import { useContext, useEffect, useState } from "react";
import classes from "../../SafeRoads.module.css";
import { Box, MenuItem, Select, Typography, useTheme } from "@mui/material";
import ReBox from "../../../../common/ReBox";
import LeaderboardTile from "../../components/LeaderboardTile";
import { GlobalContext } from "../../../../common/Context";
import LoadingWheel from "../../../../common/LoadingWheel";
import { useTranslation } from "react-i18next";
import PlacedTeamDisplay from "../PlacedTeamDisplay/PlacedTeamDisplay";
import TierNav from "../TierNav/TierNav";
import TeamBanner from "../TeamBanner/TeamBanner";
import BrowserUtils from "../../../../common/Utils/BrowserUtils";
import { stats } from "../../../../../constants";
import { purple, white } from "../../../../../theme/colours";
import { SRC_LEADERBOARD_AREA } from "../../../../../constants/ids";

const SRCLeaderboard = ({
  rankings,
  fullUserTiers,
  currTeam,
  setCurrTeam,
  currStat,
  setCurrStat,
  currTier,
  setCurrTier,
  sponsors,
  teamBanner,
}) => {
  const { t } = useTranslation("SafeRoads");
  const { mobileView, tabletView, desktopView } = useContext(GlobalContext);
  const theme = useTheme();

  const [leaderboardList, setLeaderboardList] = useState([]);
  const [userTeam, setUserTeam] = useState(null);

  const handleChange = (event) => {
    setCurrStat(event.target.value);
  };

  useEffect(() => {
    if (rankings && currTier && currStat) {
      setLeaderboardList(rankings[currTier + 1][currStat.id]);
    }
  }, [rankings, currTier, currStat]);

  useEffect(() => {
    if (leaderboardList && currTeam) {
      setUserTeam(leaderboardList.find((team) => team.isUsersScore));
    }
  }, [leaderboardList, currTeam]);

  useEffect(() => {
    if (!leaderboardList || !userTeam || BrowserUtils().isBrowserSafari) return;

    const currentIndex = leaderboardList.findIndex((team) => team.isUsersScore);

    if (currentIndex < 2) return;

    const scrollableList = document.getElementById("team-list");
    if (!scrollableList) return;
    const listItems = scrollableList.querySelectorAll("& > div");

    const itemStyles = window.getComputedStyle(listItems[1]);
    const margin = parseInt(itemStyles.marginTop);
    const height = parseInt(itemStyles.height);
    const translateY = (currentIndex - 4.5) * (height + margin);

    scrollableList.scrollTo({ top: translateY, behavior: "smooth" });
  }, [leaderboardList, userTeam]);

  const altSelectionBoxStyle = () => {
    if (mobileView || tabletView)
      return { background: "transparent", boxShadow: "none" };
  };
  const selectionBoxStyle = {
    display: "flex",
    flexDirection: "column",
    p: "0",
    width: "100%",
    mr: "1.5rem",
    borderRadius: "1rem",
    mb: (mobileView || tabletView) && "1rem",
    height: mobileView && !leaderboardList ? "275.34px" : "fit-content",
    ...altSelectionBoxStyle(),
  };
  const leaderboardContentSTyle = {
    display: "flex",
    padding: desktopView && "1.5%",
    flexDirection: "column",
    backgroundColor: desktopView && white,
    borderRadius: "1rem",
  };
  const descStyle = { opacity: 0.5, mb: "1.25rem" };
  const selectorContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignContent: "center",
    p: desktopView && "0.5rem 1.25rem",
    backgroundColor: desktopView && purple[1],
    mb: !desktopView && "1rem",
    borderRadius: "15px 15px 0 0",
  };
  const headerBoxStyle = {
    display: "flex",
  };
  const leaderTextStyle = {
    lineHeight: "2",
    fontSize: "1.25rem",
    alignSelf: "center",
    color: desktopView && white,
  };
  const selectStyle = {
    width: mobileView ? "auto" : "13rem",
    height: "2.5rem",
    alignSelf: "flex-end",
    color: white,
    fontSize: "0.875rem",
    fontWeight: 700,
    backgroundColor: purple[2],
    border: "none",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& label, & svg, & label.Mui-focused": {
      color: white,
    },
  };
  const selectDisplayStyle = {
    display: "flex",
    alignItems: "center",
  };
  const selectImageStyle = {
    width: "20px",
    height: "20px",
    mr: "0.5rem",
  };
  const leaderboardSharedBoxStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: !desktopView && "center",
    width: "100%",
  };
  const upperLeaderboardStyle = {
    display: "flex",
    width: desktopView ? "50%" : "100%",
    height: "100%",
    justifyContent:
      !desktopView &&
      (leaderboardList?.length >= 3 ? "space-between" : "center"),
    flexDirection: mobileView && "column",
    "& > div:first-child": {
      mr: !mobileView ? "1rem" : leaderboardList?.length === 2 && "2rem",
    },
    "& > div": {
      width: !mobileView
        ? `calc(100% / ${Math.min(
            3,
            desktopView ? leaderboardList?.length : 3,
          )})`
        : "100%",
    },
    "& > div:last-child": {
      mb: mobileView && 0,
      mr: tabletView ? 0 : desktopView && "1rem",
    },
  };
  const leaderboardHeaderStyle = {
    width: "100%",
    height: desktopView ? "12.5rem" : "100%",
    display: "flex",
    justifyContent: "center",
    mb: leaderboardList?.length <= 3 ? 0 : "2rem",
    flexDirection: !desktopView && "column-reverse",
    alignItems: "center",
  };
  const tierNavStyle = {
    display: "flex",
    alignItems: "center",
    width: "100%",
    overflowX: "scroll",
    pb: "0.75rem",
    mb: mobileView || tabletView ? "0.75rem" : "1.75rem",
    "&::-webkit-scrollbar": {
      width: "0px",
      display: "none",
    },
  };
  const leaderboardAreaStyle = {
    width: "100%",
    p: "0 0.75rem 0 0",
    height: "100%",
    maxHeight: "22rem",
    "& > div:first-child": { mt: 0 },
  };

  return (
    <ReBox style={selectionBoxStyle}>
      {leaderboardList && userTeam ? (
        <>
          <Box sx={selectorContainerStyle}>
            <Box sx={headerBoxStyle}>
              <img
                alt={t("trophyDesc")}
                src="/images/safeRoads/trophy.svg"
                width="35px"
              />
              <Typography variant="h2" sx={leaderTextStyle}>
                {t("leaderboardTitle")}
              </Typography>
            </Box>
            <Select
              sx={selectStyle}
              value={currStat}
              onChange={handleChange}
              renderValue={(selected) => {
                const icon = `images/mainIconsWhite/${selected.name.toLowerCase()}.svg`;
                return (
                  <Box sx={selectDisplayStyle}>
                    <Box
                      sx={selectImageStyle}
                      component="img"
                      src={icon}
                      alt={selected.name}
                      width="20px"
                    />
                    {!mobileView && selected.name}
                  </Box>
                );
              }}>
              {stats.map((stat) => (
                <MenuItem
                  sx={{ fontStyle: stat.id === -1 && "italic" }}
                  value={stat}
                  key={stat.id}>
                  {stat.id === -1 ? <em>{stat.name}</em> : stat.name}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box sx={leaderboardContentSTyle}>
            <Typography sx={descStyle}>{t("leaderboardDesc")}</Typography>
            {fullUserTiers?.length && (
              <Box sx={tierNavStyle}>
                <TierNav
                  fullUserTiers={[...fullUserTiers].reverse()}
                  currTeam={currTeam}
                  setCurrTeam={setCurrTeam}
                  setCurrTier={setCurrTier}
                />
              </Box>
            )}
            <Box sx={leaderboardSharedBoxStyle} id={SRC_LEADERBOARD_AREA}>
              <>
                <Box sx={leaderboardHeaderStyle}>
                  <Box sx={upperLeaderboardStyle}>
                    {leaderboardList
                      .slice(0, Math.min(3, leaderboardList.length))
                      .map((team, i) => (
                        <PlacedTeamDisplay
                          name={team.name ?? team.userName}
                          score={team.score}
                          icon={
                            team.name
                              ? team.icon
                              : `/images/avatars/${team.icon}.svg`
                          }
                          index={i}
                          key={i}
                          isActive={userTeam.id === team.id}
                        />
                      ))}
                  </Box>
                  <TeamBanner
                    team={currTeam}
                    sponsors={sponsors}
                    banner={teamBanner}
                  />
                </Box>
                {leaderboardList.length > 3 && (
                  <Box
                    id="team-list"
                    className={
                      leaderboardList.length > 8 &&
                      [
                        classes.scrollBoxStyle,
                        mobileView ? classes.scrollBoxStyleM : null,
                        theme.palette.mode === "dark"
                          ? classes.darkScrollBox
                          : classes.lightScrollBox,
                      ].join(" ")
                    }
                    sx={leaderboardAreaStyle}>
                    {leaderboardList.map(
                      (team, i) =>
                        i > 2 && (
                          <LeaderboardTile
                            isIcon={true}
                            name={team.name ?? team.userName}
                            score={team.score}
                            icon={
                              team.userName
                                ? `/images/avatars/${team.icon}.svg`
                                : team.icon
                            }
                            isActive={userTeam.id === team.id}
                            isUserTier={userTeam.type === "user"}
                            rank={team.rank || i + 1}
                            key={i}
                          />
                        ),
                    )}
                  </Box>
                )}
              </>
            </Box>
          </Box>
        </>
      ) : (
        <LoadingWheel width={4} />
      )}
    </ReBox>
  );
};

export default SRCLeaderboard;
